// components/Settings.tsx
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { settingsSchema, type SettingsFormData } from "../../schemas/settings";
import { api } from "../../lib/axios";

interface Settings extends SettingsFormData {
  id?: number;
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: settings || {
      name: "",
      address: "",
      phoneNumber: "",
      vat: "19",
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<Settings>("/settings");
      if (Array.isArray(response.data)) {
        setSettings({
          name: "",
          address: "",
          phoneNumber: "",
          vat: "19",
        });
        reset({
          name: "",
          address: "",
          phoneNumber: "",
          vat: "19",
        });
      } else {
        setSettings(response.data);
      }
    } catch (error) {
      setError("Failed to load settings");
      console.error("Error fetching settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: SettingsFormData) => {
    try {
      const response = await api.post<Settings>("/settings", data);
      setSettings(response.data);
      setIsEditing(false);
      setError("");
    } catch (error) {
      setError("Failed to save settings");
      console.error("Error saving settings:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full mx-auto">
      {!isEditing ? (
        <div className="bg-white shadow-sm rounded-lg p-6 border border-gray-200">
          {settings && (
            <>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">
                    Company Name
                  </span>
                  <span className="text-gray-900">{settings.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">Address</span>
                  <span className="text-gray-900">{settings.address}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 py-3 border-b border-gray-100">
                  <span className="text-gray-600 font-medium">
                    Phone Number
                  </span>
                  <span className="text-gray-900">{settings.phoneNumber}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 py-3">
                  <span className="text-gray-600 font-medium">VAT</span>
                  <span className="text-gray-900">{settings.vat}</span>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                >
                  Edit Settings
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white shadow-sm rounded-lg p-6 border border-gray-200"
        >
          <div className="space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Company Name
              </label>
              <input
                type="text"
                id="name"
                {...register("name")}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.name ? "border-red-300 bg-red-50" : "border-gray-300"
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Address
              </label>
              <input
                type="text"
                id="address"
                {...register("address")}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.address
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.address.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                {...register("phoneNumber")}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.phoneNumber
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="vat"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                VAT
              </label>
              <input
                type="tel"
                id="vat"
                {...register("vat")}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.vat ? "border-red-300 bg-red-50" : "border-gray-300"
                }`}
              />
              {errors.vat && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.vat.message}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={() => {
                  reset(settings || undefined);
                  setIsEditing(false);
                }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors duration-200 flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default Settings;
